import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Button,
  TableHead,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { useState } from "react";
import { convertTextToBytes, formatBytes, getAreas } from "../utils/ByteConverter";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TZ = "America/Los_Angeles";
const INPUT_WIDTH = 300;

const CenteredTextField = ({ value, onChange, width = INPUT_WIDTH }) => (
  <Box sx={{ display: "flex", justifyContent: "center" }}>
    <TextField value={value} onChange={onChange} sx={{ width }} />
  </Box>
);

const CenteredSelect = ({ value, onChange, options, width = INPUT_WIDTH }) => (
  <Box sx={{ display: "flex", justifyContent: "center" }}>
    <Select value={value} onChange={onChange} sx={{ width }}>
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

export const AddStoragePack = () => {
  const { authState } = useOktaAuth();

  const [ownerPrincipleName, setOwnerPrincipleName] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [expireDate, setExpireDate] = useState(dayjs().tz(LA_TZ).add(1,"year"));

  const [sizeText, setSizeText] = useState(formatBytes(10995116277760));

  const [distArea, setDistArea] = useState("");
  const [distSizeText, setDistSizeText] = useState("");
  const [distributions, setDistributions] = useState([]);

  const [processing, setProcessing] = useState(false);

  const availableAreas = getAreas(
    authState?.accessToken?.claims?.["quota_management_groups"] || []
  );

  const addDistribution = () => {
    const distSize = convertTextToBytes(distSizeText);
    if (!distArea || distSize <= 0) return;

    setDistributions((prev) => [
      ...prev,
      { areaId: distArea, size: distSize },
    ]);

    setDistArea("");
    setDistSizeText("");
  };

  const removeDistribution = (areaIdToRemove) => {
    setDistributions(distributions.filter(dist => dist.areaId !== areaIdToRemove ));
  }

  const resetForm = () => {
    setOwnerPrincipleName("");
    setSelectedAreaId(null);
    setExpireDate(dayjs().tz(LA_TZ).add(1,"year"));
    setSizeText(formatBytes(10995116277760));
    setDistributions([]);
  }

  const submitStoragePack = async() => {

    setProcessing(prev => !prev);

    let distToBeSent = distributions

    let remaingSpSize = convertTextToBytes(sizeText)

    distToBeSent.forEach(dis => {
        remaingSpSize = remaingSpSize - dis.size
    })

    distToBeSent.push({
        areaId: selectedAreaId,
        size: remaingSpSize
    });

    const res = await fetch(`${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}storage-pack`, {
        method: "POST",
        headers: {
            "Authorization": authState.accessToken.accessToken,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            owner: ownerPrincipleName,
            areaId: selectedAreaId,
            size: convertTextToBytes(sizeText),
            type: "EXTRA",
            renewalDate: expireDate.utc().format('YYYY-MM-DD HH:mm:ss'),
            distributions: distToBeSent
        })
    });

    if(!res.ok) throw Error("Failed to get a response from Storage Pack Create");

    let respBody = await res.json();

    resetForm();

    alert(respBody.message);

    setProcessing(prev => !prev);
  };

  const loader = () => {
    return(
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%", // or "100vh" if full page
              }}
            >
              <CircularProgress
                size={100}
                thickness={2}
                sx={{ color: "#A60F2D" }}
              />
            </Box>
    )
  }

  if(processing) return loader();

  return (
    <>
      <Table sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}>
        <TableHead>
            <TableRow>
                <TableCell colSpan={5} align="center" sx={{ fontWeight: "bold", fontSize: 16 }} >
                    Storage Pack Information
                </TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell align="center">Owner Principal Name</TableCell>
            <TableCell align="center">
              <CenteredTextField
                value={ownerPrincipleName}
                onChange={(e) => setOwnerPrincipleName(e.target.value)}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell align="center">Assigned Area</TableCell>
            <TableCell align="center">
              <CenteredSelect
                value={selectedAreaId}
                onChange={(e) => setSelectedAreaId(e.target.value)}
                options={availableAreas}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell align="center">Size</TableCell>
            <TableCell align="center">
              <CenteredTextField
                value={sizeText}
                onChange={(e) => setSizeText(e.target.value)}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell align="center">Expiration Date</TableCell>
            <TableCell align="center">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={expireDate}
                  timezone={LA_TZ}
                  onChange={(newValue) => {
                    if (newValue?.isValid()) setExpireDate(newValue);
                  }}
                  slotProps={{
                    textField: { sx: { width: INPUT_WIDTH } },
                  }}
                />
              </LocalizationProvider>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <hr />

      <Table sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}>
        <TableHead>
            <TableRow>
                <TableCell colSpan={5} align="center" sx={{ fontWeight: "bold", fontSize: 16 }}>
                    Add Distribution
                </TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell align="center">Distribution Area</TableCell>
            <TableCell align="center">
              <CenteredSelect
                value={distArea}
                onChange={(e) => setDistArea(e.target.value)}
                options={availableAreas}
                width={200}
              />
            </TableCell>

            <TableCell align="center">Size</TableCell>
            <TableCell align="center">
              <CenteredTextField
                value={distSizeText}
                onChange={(e) => setDistSizeText(e.target.value)}
                width={200}
              />
            </TableCell>

            <TableCell align="center">
              <IconButton
                variant="contained"
                onClick={addDistribution}
                disabled={!distArea || !distSizeText}
                style={{backgroundColor: "#A60F2D", color: "white"}}
              ><Add /></IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <hr />

      <Table>
        <TableHead>
          <TableRow>
            <TableCell colSpan={5} align="center" sx={{ fontWeight: "bold", fontSize: 16 }}>
                Distributions
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="center">Distribution Area</TableCell>
            <TableCell align="center">Distribution Size</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {distributions.map((dist, i) => (
            <TableRow key={`${dist.areaId}-${i}`}>
              <TableCell align="center">{dist.areaId}</TableCell>
              <TableCell align="center">{formatBytes(dist.size)}</TableCell>
              <TableCell>
                <IconButton
                    variant="contained"
                    onClick={() => removeDistribution(dist.areaId)}
                    style={{backgroundColor: "#A60F2D", color: "white"}}
                >
                    <Remove />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button variant="contained" onClick={submitStoragePack} style={{backgroundColor: "#A60F2D", color: "white", margin: "30px"}}
        disabled={ownerPrincipleName === "" || selectedAreaId === null}
        >
          Submit Storage Pack
        </Button>
        <Button variant="contained" onClick={resetForm} style={{backgroundColor: "#A60F2D", color: "white", margin: "30px"}}>
          Clear
        </Button>
      </Box>
    </>
  );
};
